import { TranslatePipe } from "@ngx-translate/core";
import { Apollo } from "apollo-angular";
import { firstValueFrom } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, input, resource, signal } from "@angular/core";
import { Sort } from "@angular/material/sort";

import {
  DnsFirewallSearchFilter,
  DnsFirewallSort,
  DnsFirewallTypeConnection,
} from "../../../../apis/networkdashboard/graphql/graphql";
import { DNS_FIREWALL_QUERY } from "../../../../apis/networkdashboard/static/graphql-queries/dns-firewall";
import { CertChartGeneric } from "../../../../components/cert/cert-chart-generic/cert-chart";
import { CertDNSFirewallTable } from "../../../../components/cert/cert-dns-firewall-table/cert-dns-firewall-table";
import { CertFilterDns } from "../../../../components/cert/cert-filter-dns/cert-filter";
import { AnalyticsService } from "../../../../services/analytics.service";
import {
  AnalyticsAction,
  AnalyticsModule,
  CertLogInteraction,
  CertLogView,
} from "../../../../services/analytics.types";
import { AuthService } from "../../../../services/authservice";
import { chartDataTransform, chartLegendTransform } from "./chart-data-transform";

type FetchArguments = {
  filter: DnsFirewallSearchFilter;
  paginationAfter?: number;
  sort: DnsFirewallSort;
  first?: number;
};

const EMPTY_PAGE_INFO = {
  filterFields: [],
  hasNextPage: false,
  hasPreviousPage: false,
  sortFields: [],
  endCursor: 0,
  startCursor: 0,
  totalItems: 0,
};

@Component({
  selector: "dns-firewall-tab",
  templateUrl: "dns-firewall-tab.html",
  styleUrl: "../../cert.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CertDNSFirewallTable, CertChartGeneric, CertFilterDns, TranslatePipe],
})
export class DnsFirewallTab {
  private apollo = inject(Apollo);
  private auth = inject(AuthService);
  private analytics = inject(AnalyticsService);

  public prefixes = input.required<string[]>();
  private filterInitialised = false;

  private trackInspection(interaction: CertLogInteraction) {
    this.analytics.trackFeature(
      AnalyticsModule.Cert,
      AnalyticsAction.InspectCertLog,
      `${CertLogView.DnsFirewall}:${interaction}`,
    );
  }

  protected dnsFirewallResults = resource({
    params: () => ({
      filter: { ...this.searchFilter(), customerId: this.accessCustomerId() },
      paginationAfter: this.paginationAfter(),
      sort: this.querySort(),
      first: this.itemsPerPage(),
    }),
    loader: async ({ params }) => {
      const result = await this.fetchDnsFirewallInformation(params);
      return result.data?.dnsFirewallLogs ?? { edges: [], pageInfo: EMPTY_PAGE_INFO };
    },
    defaultValue: { edges: [], pageInfo: EMPTY_PAGE_INFO },
  });

  protected legendResults = resource({
    params: () => ({
      filter: { ...this.searchFilter(), customerId: this.accessCustomerId() },
      sort: this.querySort(),
    }),
    loader: async ({ params }) => {
      const result = await this.fetchDnsFirewallInformation({ ...params, first: 99999 });
      return result.data?.dnsFirewallLogs?.edges ?? [];
    },
  });
  protected loadingState = computed(() => this.dnsFirewallResults.isLoading() || this.legendResults.isLoading());

  protected searchFilter = signal<DnsFirewallSearchFilter>({});
  private itemsPerPage = signal(Number(localStorage.getItem("paginationSize") ?? 10));
  protected pageInfo = computed(() =>
    this.dnsFirewallResults.hasValue() ?
      (this.dnsFirewallResults.value().pageInfo ?? EMPTY_PAGE_INFO)
    : EMPTY_PAGE_INFO,
  );
  protected dnsFirewallLogs = computed(() =>
    this.dnsFirewallResults.hasValue() ? (this.dnsFirewallResults.value().edges ?? []) : [],
  );

  private paginationAfter = signal(0);
  private sortInfo = signal<Sort>({
    active: "recordedAt",
    direction: "desc",
  });
  private querySort = computed(() => ({
    order: [{ [this.sortInfo().active || "recordedAt"]: this.sortInfo().direction.toUpperCase() || "DESC" }],
  }));
  private accessCustomerId = computed(() => this.auth.viewingCustomer().customerId);

  protected dnsFirewallChartSeries = computed(() => chartDataTransform(this.dnsFirewallLogs()));
  protected dnsFirewallChartLegendGroups = computed(() => chartLegendTransform(this.legendResults.value() ?? []));

  private fetchDnsFirewallInformation(args: FetchArguments) {
    return firstValueFrom(
      this.apollo.query<{ dnsFirewallLogs: DnsFirewallTypeConnection }>({
        query: DNS_FIREWALL_QUERY,
        fetchPolicy: "network-only",
        variables: {
          filter: args.filter,
          first: args.first ?? 10,
          after: args.paginationAfter ?? 0,
          sort: args.sort,
        },
      }),
    );
  }

  protected onFiltersChanged(event: DnsFirewallSearchFilter) {
    this.searchFilter.set(event);
    if (this.filterInitialised) {
      // CertFilterDns emits on init via an effect; skip the first emission
      // so only deliberate user filtering is tracked
      this.trackInspection(CertLogInteraction.Filter);
    }
    this.filterInitialised = true;
  }

  protected onPaginationChanged(event: number) {
    this.paginationAfter.set(event > -1 ? event : 0);
    this.trackInspection(CertLogInteraction.Paginate);
  }

  protected onSortingChanged(event: Sort) {
    this.sortInfo.set(event);
    this.trackInspection(CertLogInteraction.Sort);
  }

  protected onPageSizeChanged(event: number) {
    this.itemsPerPage.set(event);
    this.trackInspection(CertLogInteraction.Paginate);
  }
}
